import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient } from '@tanstack/react-query';
import { createBrowserRouter } from 'react-router-dom';
import type { IWhiteLabelConfiguration } from '@shore/types';
import { UAParser } from 'ua-parser-js';

import './styles/index.css';
import Router from './Router';
import Tracer from './utils/tracer';
import Logger from './utils/logger';
import FeatureRollout from './utils/feature-rollout';
import createRouter from './deprecated-router';
import initI18next, { setupTranslations } from './initializers/i18next';
import initMomentJs from './initializers/moment';
import configureApiClient, { createApiClient } from './initializers/api-client';
import getGlobalValue from './utils/get-global-value';
import createStore from './store';
import * as defaultSubscriptions from './subscriptions';
import loadApp from './app-manifest/app-loader';
import { fetchSubscription } from './utils/fetch-subscription';
import { fetchMerchantPaymentSettings } from './utils/fetch-merchant-payment-settings';
import { AppProviders } from './app/providers/AppProviders';
import {
  initializeSuccess,
  notificationSMSDisplaySeverity,
  setupBrowserLocaleAndErrorState,
} from './actions';
import { localStorage, sessionStorage } from './utils/namespaced-storage';
import { createAsyncScriptNode } from './utils/dom';
import createTrackMethod, {
  type AnalyticsInstance,
} from './initializers/track-method';
import createErrorLogger, {
  type SentryClient,
} from './initializers/error-logger';
import { createEventBus } from './initializers/event-bus';
import preLoadApp from './app-manifest/pre-loader';
import { values } from './utils/object';
import { SEVERITY, CELLO_PRODUCT_ID, API_ENV } from './constants';
import { document } from './env/browser';
import { keys as queryKeys } from './queries';
import { getUserRole } from './utils/user';
import { getValue } from './utils/object';
import type {
  AppShellApiClient,
  BookingConfigurations,
  CurrentMerchantData,
  GlobalsTools,
  SessionPermissionsPayload,
} from './shared/types';
import type { AppDispatch } from './store';

Tracer.init();
FeatureRollout.activate('api-consumption-analysis', { percentage: 30 });

// Populated below via Object.assign(apiClient, await configureApiClient(...))
// once the async bootstrap completes — configureApiClient's actual return
// shape is what makes AppShellApiClient accurate; this placeholder exists
// only so `deps`/`apiClient` have a stable reference to mutate into.
const apiClient = {} as AppShellApiClient;
const errorLogger = createErrorLogger(
  getGlobalValue('Sentry') as SentryClient | undefined
);
const router = createRouter();
const eventBus = createEventBus();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});
const trackMethod = createTrackMethod(
  queryClient,
  () =>
    getGlobalValue('analytics') as
      | Partial<AnalyticsInstance>
      | null
      | undefined,
  errorLogger
);
const deps: GlobalsTools = {
  apiClient,
  trackMethod,
  router,
  sessionStorage,
  localStorage,
  errorLogger,
  eventBus,
  queryClient,
  // GlobalsTools['loadApp'] declares a synchronous create(); the real
  // create() is async — confirmed in app-loader.test.ts, which awaits it.
  // Unverified: whether the resolved value actually has .destroy()/
  // .dispatchAction() like the sync type assumes — that depends on a real
  // micro-app's own create() (lives in a different repo). Pre-existing
  // mismatch, not introduced here; needs its own follow-up.
  loadApp: loadApp as unknown as GlobalsTools['loadApp'],
};

// createStore's param type carries an index signature (to accept the
// narrower per-effect dep shapes tests construct) that plain interfaces
// aren't assignable to without one of their own — intersect rather than
// touch that shared type, so this stays local to the one full-shape caller.
const store = createStore(deps as GlobalsTools & Record<string, unknown>);

const rootElement = document.getElementById('root') as HTMLElement;
const root = ReactDOM.createRoot(rootElement);

root.render(
  <AppProviders store={store} tools={deps}>
    <Router create={createBrowserRouter} />
  </AppProviders>
);

// Cello script should be loaded after `render` function from above.
// Otherwise, Cello gives an error "Could not find Shadow Root"
// for the payment screen in the widget
if (CELLO_PRODUCT_ID) {
  const celloScriptSrc =
    API_ENV === 'production'
      ? 'https://assets.cello.so/app/v2/latest/cello.js'
      : 'https://assets.sandbox.cello.so/app/v2/latest/cello.js';

  document.head.appendChild(
    createAsyncScriptNode({
      src: celloScriptSrc,
      type: 'module',
    })
  );
}

interface SmsUsageEvent {
  data: {
    attributes: {
      current_usage: string;
    };
  };
}

const smsDisplayNofification = (
  dispatch: AppDispatch,
  {
    data: {
      attributes: { current_usage },
    },
  }: SmsUsageEvent
): void => {
  switch (current_usage) {
    case '80%': {
      dispatch(notificationSMSDisplaySeverity(SEVERITY.WARNING));
      break;
    }
    case '100%': {
      dispatch(notificationSMSDisplaySeverity(SEVERITY.DANGER));
      break;
    }
    default: {
      dispatch(notificationSMSDisplaySeverity(SEVERITY.NONE));
    }
  }
};

(async function initialize() {
  try {
    Object.assign(
      apiClient,
      await configureApiClient(createApiClient, queryClient)
    );
    const {
      websocketListener,
      observeUnreadNotificationCount,
      ...subscriptions
    } = defaultSubscriptions;

    values(subscriptions).forEach((fn) => fn(store));

    const myAccountData = await apiClient.getMyAccountData();
    // getCurrentMerchantData/getSessionPermissions/getBookingConfigs are
    // typed `| undefined` on AppShellApiClient because their backing
    // variables in initializers/api-client.ts's configureApiClient are only
    // conditionally assigned — but every path that fails to assign them
    // throws before configureApiClient returns (see that file), so by the
    // time this apiClient is live, they're always populated.
    const currentMerchantData =
      (await apiClient.getCurrentMerchantData()) as CurrentMerchantData;
    const whiteLabelConfig = await apiClient.getWhiteLabelConfig();
    const sessionPermissions =
      (await apiClient.getSessionPermissions()) as SessionPermissionsPayload;
    // @shore/types' IBookingConfigResponse is missing
    // shorepay_online_activated_at (a real, more-complete field the backend
    // returns) — same kind of staleness gap as IContract.state below.
    const bookingConfigs =
      (await apiClient.getBookingConfigs()) as unknown as BookingConfigurations;

    const subscription = await fetchSubscription(
      currentMerchantData.id,
      apiClient,
      queryClient
    );
    const paymentSettings = await fetchMerchantPaymentSettings({
      merchantId: currentMerchantData.id,
      featureToggles: currentMerchantData.feature_toggles,
      apiClient,
      queryClient,
      errorLogger,
    });

    const organizationId = myAccountData.organization.id;

    const { browser, device } = UAParser();

    const sessionRole = getUserRole({
      merchantId: currentMerchantData.id,
      roles: myAccountData.roles,
    });

    if (!sessionRole) {
      errorLogger.log(new Error('Session role not found at initialization'), {
        merchantId: currentMerchantData.id,
        employee: JSON.stringify(myAccountData.employee),
        roles: JSON.stringify(myAccountData.roles),
      });
    }

    const whitelabel =
      getValue(
        currentMerchantData as unknown as Record<string, unknown>,
        'organization.whitelabel_slug',
        null
      ) || 'shore';

    queryClient.setQueryDefaults(queryKeys.trackingSessionMetadata, {
      gcTime: Infinity,
    });

    queryClient.setQueryData(queryKeys.trackingSessionMetadata, {
      whitelabel,
      has_subscription: !!subscription,
      subscription_status: subscription?.status,
      subscription_plan: subscription?.plan.id,
      subscription_since: subscription?.started_at,
      session_browser: browser.name,
      session_device_model: device.model,
      session_device_type: device.type,
      session_vendor: device.vendor,
      session_organization_id: organizationId,
      session_merchant_id: currentMerchantData.id,
      session_role: sessionRole,
      merchant_id: currentMerchantData.id,
    });

    const contracts = await apiClient.contractV2.findAll({
      filter: { organization: organizationId },
      include: ['packages', 'fees'],
    });

    const activeContract =
      contracts?.filter(
        // @shore/types' IContract.state only models 'accepted' | 'cancelled',
        // but the real API also returns 'terminated' — widen for this check.
        (c) => c.state === 'accepted' || (c.state as string) === 'terminated'
      )[0] || null;

    const packages =
      activeContract?.packages.filter(
        (p) => p.category_code !== 'shore_app_marketplace'
      ) || null;

    const packageCode =
      Array.isArray(packages) && packages.length > 0
        ? packages[0].package_code
        : null;

    observeUnreadNotificationCount(store, deps, currentMerchantData.id);
    websocketListener(apiClient)(myAccountData.id)(
      'private-channel.sms_monthly_allowance.merchant_accounts'
    )('sms_monthly_allowance.v1.limit_reached')((d) => {
      smsDisplayNofification(store.dispatch, d as SmsUsageEvent);
    });

    // For Testing
    // const dummyData = { data: { attributes: { current_usage: '100%' } } };

    // setInterval(() => {
    //   smsDisplayNofification(store.dispatch, dummyData);
    // }, 15000);

    const { locale } = myAccountData;

    Tracer.setUser({
      merchantId: currentMerchantData.id,
      employeeId: myAccountData.employee.id,
    });

    Tracer.setGlobalContext('has_chargebee_subscription', !!subscription);

    Logger.init({
      merchantId: currentMerchantData.id,
      employeeId: myAccountData.employee.id,
    });

    await initMomentJs(locale);
    await initI18next(locale);
    await setupTranslations(locale);

    preLoadApp('search', locale, eventBus);
    preLoadApp('launchpad', locale, eventBus);

    store.dispatch(
      initializeSuccess(
        myAccountData,
        currentMerchantData,
        // getWhiteLabelConfig honestly returns Partial<...> (falls back to
        // {} on fetch failure — see initializers/api-client.ts), but
        // initializeSuccess's payload type wants the full shape; pre-existing
        // tension between those two already-typed files, not introduced here.
        whiteLabelConfig as IWhiteLabelConfiguration,
        packageCode,
        sessionPermissions,
        // fetchSubscription returns SubscriptionResponse | null,
        // initializeSuccess's payload wants | undefined — same "no
        // subscription" meaning either way.
        subscription ?? undefined,
        sessionRole,
        paymentSettings,
        bookingConfigs
      )
    );
  } catch (error) {
    store.dispatch(setupBrowserLocaleAndErrorState(error));
  }
})();
